import { Container, IInit } from '../../../common/container/Container'
import { ContentContainerConfig } from '../container'
import { Observable, of } from 'rxjs'
import { Query } from '../../../common/api/Query'
import { catchError, map } from 'rxjs/operators'
import { prepareURL } from '../../../common/api/http-helpers'
import { HTTP_CLIENT_KEY, IHTTPClient } from '../../../common/api/HTTPClient'
import { IStatusService } from '../../../common/status/StatusService'
import { emptyList, ItemList } from '../../../common/models/ItemList'
import { STATUS_SERVICE_KEY } from '../../../container/app'
import { Intensity } from '../models/Intensity'
import { toModel, IntensityDTO } from '../models/IntensityDTO'

export interface IUserIntensityApi extends IInit {
  getByID(id: string): Observable<Intensity | undefined>

  getFilteredList(q: Query<Intensity>): Observable<ItemList<Intensity>>

  add(e: IntensityDTO): Observable<Intensity | undefined>

  update(e: IntensityDTO): Observable<Intensity | undefined>

  delete(id: string): Observable<boolean>
}

export class UserIntensityApi implements IUserIntensityApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url = (this._container.config as ContentContainerConfig).moduleFullUrl + '/userIntensity'
  }

  add(e: IntensityDTO): Observable<Intensity | undefined> {
    return this._httpClient
      .post<Intensity>({ url: this._url, body: e })
      .pipe(
        map<IntensityDTO, Intensity>((d) => {
          this._statusService.sendStatus({ variant: 'success' })
          return toModel(d)
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }

  delete(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: `${this._url}/${id}` }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(false)
      })
    )
  }

  getByID(id: string): Observable<Intensity | undefined> {
    return this._httpClient
      .get<Intensity>({ url: `${this._url}/${id}` })
      .pipe(
        map<IntensityDTO, Intensity>((d) => toModel(d)),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }

  getFilteredList(q: Query<Intensity>): Observable<ItemList<Intensity>> {
    let url = this._url
    return this._httpClient
      .get<ItemList<Intensity>>({ url: prepareURL(url, q) })
      .pipe(
        map<ItemList<IntensityDTO>, ItemList<Intensity>>((dto) => {
          const itemList = emptyList<Intensity>()
          itemList.count = dto.count
          itemList.items = dto.items.map((d) => toModel(d))
          return itemList
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(emptyList<Intensity>())
        })
      )
  }

  update(e: IntensityDTO): Observable<Intensity | undefined> {
    return this._httpClient
      .put<Intensity>({ url: this._url, body: e })
      .pipe(
        map<IntensityDTO, Intensity>((d) => {
          this._statusService.sendStatus({ variant: 'success' })
          return toModel(d)
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }
}
